import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, X, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { api } from "@/lib/api";

// ClassPass reviews carousel + "See all" modal. Pulls featured reviews from backend.
// Auto-rotates every 5s, pauses on hover, mobile-swipeable via scroll-snap.
function StarRow({ rating = 5, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={0}
          className={i < rating ? "fill-yellow-400 text-yellow-400" : "text-white/15"}
        />
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { month: "short", year: "numeric" });
}

function ReviewCard({ r, compact = false }) {
  return (
    <article
      className={`relative border border-white/10 bg-black/40 p-6 sm:p-8 h-full flex flex-col ${
        compact ? "min-h-[220px]" : "min-h-[280px]"
      }`}
      data-testid={`classpass-review-${r.id}`}
    >
      <Quote className="text-[#005EEA]/40 mb-4" size={32} strokeWidth={1.5} />
      <StarRow rating={r.rating} />
      <p className="text-white/85 text-sm sm:text-base leading-relaxed mt-4 flex-1">
        &ldquo;{r.review}&rdquo;
      </p>
      <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-white/70 text-xs font-mono-accent uppercase tracking-wider">
            {r.reviewer}
          </div>
          {r.class_name && (
            <div className="text-[#005EEA] text-[11px] font-mono-accent mt-1">
              {r.class_name}
            </div>
          )}
        </div>
        <div className="text-white/40 text-[10px] font-mono-accent">
          {formatDate(r.date)} · VERIFIED
        </div>
      </div>
    </article>
  );
}

export default function ClassPassReviews() {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/classpass/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/classpass/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  // Auto-rotate featured carousel every 5s (pauses on hover / when modal open)
  useEffect(() => {
    if (paused || modalOpen || reviews.length < 2) return;
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(t);
  }, [paused, modalOpen, reviews.length]);

  if (reviews.length === 0) return null;

  // Show 3 cards at a time on desktop; wrap around
  const visible = [
    reviews[activeIdx % reviews.length],
    reviews[(activeIdx + 1) % reviews.length],
    reviews[(activeIdx + 2) % reviews.length],
  ];

  return (
    <section
      id="classpass"
      className="relative py-24 sm:py-32 bg-[#050505] border-t border-white/5 overflow-hidden"
      data-testid="classpass-section"
    >
      {/* Blue glow accent */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#005EEA]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        {/* Header w/ ratings summary */}
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-3">
                — 08 · MEMBER REVIEWS
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.9] uppercase">
                Rated <span className="text-[#005EEA]">5.0★</span>
                <br />
                across{" "}
                <span className="text-[#005EEA]">
                  {summary ? summary.total_ratings : 154}+
                </span>{" "}
                ratings.
              </h2>
              <p className="text-white/60 text-sm sm:text-base max-w-md mt-6">
                {summary ? summary.five_star : 150} five-star ratings from real bootcamp
                members. Below are a few of their words.
              </p>
            </div>

            {summary && (
              <div className="border border-white/10 bg-black/40 p-6 min-w-[280px]" data-testid="classpass-summary">
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="font-display text-5xl text-white leading-none">
                    {summary.overall_rating.toFixed(1)}
                  </span>
                  <StarRow rating={5} size={18} />
                </div>
                <div className="space-y-1.5">
                  {[
                    ["5★", summary.five_star],
                    ["4★", summary.four_star],
                    ["3★", summary.three_star],
                    ["2★", summary.two_star],
                    ["1★", summary.one_star],
                  ].map(([label, count]) => {
                    const pct =
                      summary.total_ratings > 0
                        ? (count / summary.total_ratings) * 100
                        : 0;
                    return (
                      <div key={label} className="flex items-center gap-3 text-[11px]">
                        <span className="text-white/50 w-6 font-mono-accent">{label}</span>
                        <div className="flex-1 h-1.5 bg-white/5 relative overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 bg-[#005EEA]"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-white/50 w-10 text-right font-mono-accent">
                          {count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Reveal>

        {/* Rotating featured carousel — mobile scroll-snap, desktop 3-up */}
        <Reveal delay={0.05}>
          <div
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="classpass-carousel"
          >
            {/* Mobile: horizontal scroll snap through ALL featured reviews */}
            <div className="lg:hidden -mx-4 overflow-x-auto snap-x snap-mandatory scroll-smooth flex gap-4 px-4 pb-4">
              {reviews.map((r) => (
                <div key={r.id} className="snap-start shrink-0 w-[85%] sm:w-[60%]">
                  <ReviewCard r={r} />
                </div>
              ))}
            </div>

            {/* Desktop: 3-up with auto-rotation */}
            <div className="hidden lg:grid grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {visible.map((r) => (
                  <motion.div
                    key={r.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <ReviewCard r={r} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>

        {/* Actions */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
            <div className="flex gap-1.5" data-testid="classpass-dots">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  aria-label={`Show review ${i + 1}`}
                  className={`h-1 transition-all ${
                    i === activeIdx % reviews.length ? "w-8 bg-[#005EEA]" : "w-4 bg-white/15"
                  }`}
                  data-testid={`classpass-dot-${i}`}
                />
              ))}
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="font-mono-accent text-xs uppercase tracking-[0.2em] border border-white/20 text-white px-6 py-3 hover:border-white hover:bg-white/5 transition-colors flex items-center gap-2"
              data-testid="classpass-see-all"
            >
              See all {reviews.length} reviews <ChevronRight size={14} />
            </button>
          </div>
        </Reveal>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            data-testid="classpass-modal"
            onClick={() => setModalOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
              className="relative w-full max-w-4xl bg-[#0a0a0a] border border-white/10 my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-6 py-5 flex items-center justify-between z-10">
                <div>
                  <div className="font-mono-accent text-[10px] text-[#005EEA] tracking-[0.3em]">
                    — ALL MEMBER REVIEWS
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl uppercase leading-tight mt-1">
                    {reviews.length} written reviews ·{" "}
                    <span className="text-[#005EEA]">
                      {summary ? summary.total_ratings : 154}
                    </span>{" "}
                    total ratings
                  </h3>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  aria-label="Close"
                  data-testid="classpass-modal-close"
                  className="text-white/50 hover:text-white transition-colors"
                >
                  <X size={22} />
                </button>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {reviews.map((r) => (
                  <ReviewCard key={r.id} r={r} compact />
                ))}
              </div>
              <p className="px-6 pb-6 text-center text-white/40 text-xs font-mono-accent">
                Reviews shown are verified from real members who attended these classes.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
